import { watch, toValue, ref, computed } from 'vue'
import { useAsyncState, type MaybeRefOrGetter } from '@vueuse/core'
import { calculateRefund } from '@/lib/refunds/remote'

/**
 * Reactive helper that keeps `amount` in sync with the backend calculator.
 *
 * @param year   – ref or primitive tax year
 * @param category – Form 11 refund category key (exact string as backend expects)
 * @param payload  – reactive object / ref with the category data
 * @param options  – passthrough to useAsyncState (e.g. debounce). `resetOnExecute` is forced to `false` so we keep the last value while a new one is loading.
 */
export function useCategoryRefund<T extends object>(
  year: MaybeRefOrGetter<number>,
  category: string,
  payload: MaybeRefOrGetter<T>,
  options: Parameters<typeof useAsyncState>[2] = {},
) {
  // Merge caller-supplied options with defaults.
  const mergedOptions = {
    immediate: true,
    // Keep the previous value while the request is pending.
    resetOnExecute: false,
    ...options,
  }

  const {
    state: amount,
    isLoading,
    error,
    execute,
  } = useAsyncState(
    () =>
      calculateRefund(
        toValue(year) as number,
        category,
        toValue(payload) as any,
      ),
    0,
    mergedOptions,
  )

  // Re-execute whenever year or payload changes.
  watch(
    [
      () => toValue(year),
      () => toValue(payload),
    ],
    () => {
      execute()
    },
  )

  return {
    amount,
    isLoading,
    error,
    refresh: execute,
  }
}
